import clsx from "clsx";
import { DateFormat, now } from "common";
import { Dayjs } from "dayjs";
import React, { Fragment } from "react";
import {
    AllowedPrivacy,
    BaseSchedule,
    CouponAlgorithm,
    CouponScope,
    LineItemTemplate,
    Membership,
    MembershipUsageType,
    PurchaseRule,
    PurchaseRuleShortcutType,
    SelectionState,
} from "types";
import { Currency } from "@/components/Common/Currency";
import { getProductRestrictions } from "@/components/Product/EditGiftOfferRestrictions";
import {
    getMembershipDiscountBenefits,
    MembershipDiscountBenefits,
} from "@/components/Product/Membership/MembershipBenefits/MembershipBenefits";
import { RedemptionRuleTransformer } from "@/components/Product/Membership/MembershipRestrictions/MembershipRedemptionRule.transformer";
import { getScheduleAndBlackoutRestrictions } from "@/components/Product/Membership/MembershipRestrictions/MembershipRestrictions";
import {
    ProductRestrictionsList,
    PurchaseRuleTile,
    ScheduleRestrictions,
} from "@/components/PurchaseCreate/CreatePurchaseProductRestrictions";
import { useMembershipRedemptionRule } from "@/components/PurchaseCreate/hooks/useMembershipRedemptionRule";
import { useResource } from "@/hooks/useResource";

interface CreatePurchaseMembershipTermsProps {
    membership: Membership;
}

export interface MembershipFormTerms extends MembershipDiscountBenefits {
    price: number;
    additionalBenefits: string | null;
    privacies: AllowedPrivacy[];
    usageLimit: number | null;
    usageType: MembershipUsageType;
    expiryDate: string | null;
    productRestrictions: string[];
    organizerRequired: boolean;
    purchaseWindowSchedules: BaseSchedule[];
    purchaseWindowBlackoutSchedules: BaseSchedule[];
    arrivalWindowSchedules: BaseSchedule[];
    arrivalWindowBlackoutSchedules: BaseSchedule[];
}

export const CreatePurchaseMembershipTerms = ({ membership }: CreatePurchaseMembershipTermsProps) => {
    const { data: redemptionPurchaseRule } = useMembershipRedemptionRule(membership);
    if (!redemptionPurchaseRule) return null;

    const terms = getTerms(membership, redemptionPurchaseRule);

    return (
        <>
            <div className="space-y-1">
                <h4 className="font-bold">Benefits</h4>
                <MembershipBenefitsTiles isWide={false} terms={terms} />
            </div>
            <div className="space-y-1">
                <h4 className="font-bold">Restrictions</h4>
                <MembershipRestrictionsTiles redemptionPurchaseRule={redemptionPurchaseRule} terms={terms} />
            </div>
        </>
    );
};

interface MembershipBenefitsProps {
    isWide?: boolean; // eslint-disable-line react/no-unused-prop-types
    terms: MembershipFormTerms;
}

export const MembershipBenefitsTiles = ({ terms, isWide = true }: MembershipBenefitsProps) => {
    const hasOnlyAdditionalBenefits =
        terms.algorithm === CouponAlgorithm.ABSOLUTE && terms.scope === CouponScope.PER_BOOKING && !terms.amount;

    return (
        <div className={clsx("grid gap-1", isWide && !hasOnlyAdditionalBenefits ? "grid-cols-2" : "grid-cols-1")}>
            {!hasOnlyAdditionalBenefits && (
                <PurchaseRuleTile title="Discount Benefit">
                    <span>
                        {terms.algorithm === CouponAlgorithm.ABSOLUTE ? (
                            <Currency>{terms.amount}</Currency>
                        ) : (
                            `${terms.amount}%`
                        )}
                        &nbsp;off per&nbsp;
                        {terms.scope === CouponScope.PER_BOOKING ? "purchase" : <PerPersonBenefitTerm terms={terms} />}
                    </span>
                </PurchaseRuleTile>
            )}

            <PurchaseRuleTile title="Additional Benefits">
                <span>{terms.additionalBenefits ?? "Not available"}</span>
            </PurchaseRuleTile>
        </div>
    );
};

interface MembershipRestrictionsProps {
    isWide?: boolean;
    terms: MembershipFormTerms;
    redemptionPurchaseRule: PurchaseRule;
}

export const MembershipRestrictionsTiles = ({
    terms,
    redemptionPurchaseRule,
    isWide = false,
}: MembershipRestrictionsProps) => {
    const productRestrictions = getProductRestrictions(redemptionPurchaseRule, "product.id");

    return (
        <div className="space-y-1">
            <PurchaseRuleTile title="Product Restrictions">
                <ProductRestrictionsList productRestrictions={productRestrictions} type="text" />
            </PurchaseRuleTile>

            <PurchaseRuleTile title="Arrival Restrictions">
                <ScheduleRestrictions
                    available={terms.arrivalWindowSchedules}
                    blackout={terms.arrivalWindowBlackoutSchedules}
                />
            </PurchaseRuleTile>

            <PurchaseRuleTile title="Purchase Date Restrictions">
                <ScheduleRestrictions
                    available={terms.purchaseWindowSchedules}
                    blackout={terms.purchaseWindowBlackoutSchedules}
                />
            </PurchaseRuleTile>

            <div className={clsx("grid gap-1", isWide ? "grid-cols-3" : "grid-cols-2")}>
                <PurchaseRuleTile title="Privacy Restrictions">
                    <span className="capitalize">{terms.privacies.length === 1 ? terms.privacies[0] : "None"}</span>
                </PurchaseRuleTile>
                <PurchaseRuleTile title="Usage Limit">
                    <span>{terms.usageType === MembershipUsageType.UNLIMITED ? "Unlimited" : terms.usageLimit}</span>
                </PurchaseRuleTile>
                <PurchaseRuleTile title="Expiry Date">
                    <span>{terms.expiryDate ?? "No Expiry"}</span>
                </PurchaseRuleTile>
            </div>
        </div>
    );
};

const PerPersonBenefitTerm = ({ terms }: MembershipBenefitsProps) => {
    return (
        <>
            person for&nbsp;
            <DemographicsList demographics={terms.demographics} />
            {terms.demographicsLimit > 0 && ` Max ${terms.demographicsLimit} guests eligible for discount.`}
        </>
    );
};

const DemographicsList = ({ demographics }: { demographics: SelectionState }) => {
    if (demographics.all) {
        return <>all demographics.</>;
    }

    return (
        <>
            {demographics.selected.length} demographics (
            {demographics.selected.map((id, index) => (
                <Fragment key={id}>
                    <DemographicName key={id} id={id} />
                    {index !== demographics.selected.length - 1 && ", "}
                </Fragment>
            ))}
            ).
        </>
    );
};

const DemographicName = ({ id }: { id: ID }) => {
    const { data: template, isLoading } = useResource<LineItemTemplate>(["lineItemTemplates", id]);
    return isLoading ? <span>Loading</span> : <span>{template?.name ?? "N/A"}</span>;
};

export const getTerms = (
    membership: Membership,
    redemptionPurchaseRule: PurchaseRule,
    purchaseDate?: Dayjs,
): MembershipFormTerms => {
    const terms: MembershipFormTerms = {
        additionalBenefits: membership.additionalBenefits ?? null,
        expiryDate: null,
        price: membership.price,
        privacies: [],
        usageLimit: null,
        organizerRequired: RedemptionRuleTransformer.isOrganizerRequired(redemptionPurchaseRule),
        usageType: MembershipUsageType.UNLIMITED,
        productRestrictions: getProductRestrictions(redemptionPurchaseRule, "product.id"),
        ...getScheduleAndBlackoutRestrictions(redemptionPurchaseRule),
        ...getMembershipDiscountBenefits(redemptionPurchaseRule),
    };

    if (redemptionPurchaseRule?.actions?.length) {
        const [action] = redemptionPurchaseRule.actions;
        const { shortcut } = action;

        if (shortcut) {
            terms.algorithm =
                shortcut.type === PurchaseRuleShortcutType.PERCENT ? CouponAlgorithm.PERCENT : CouponAlgorithm.ABSOLUTE;
            terms.amount = Math.abs(shortcut.amount);
        }
    }

    if (redemptionPurchaseRule) {
        terms.privacies = RedemptionRuleTransformer.getAllowedPrivacies(redemptionPurchaseRule) ?? [];

        const { usageLimit, usageType } = RedemptionRuleTransformer.getUsage(redemptionPurchaseRule);
        terms.usageType = usageType;
        terms.usageLimit = usageLimit;

        const expiryInterval = RedemptionRuleTransformer.getExpiry(redemptionPurchaseRule);
        terms.expiryDate = expiryInterval
            ? now(purchaseDate).add(expiryInterval, "minutes").format(DateFormat.DATE_LONG)
            : null;
    }

    return terms;
};
